import { Button, Stack, Text, Tooltip } from '@mantine/core';
import { DeepKeys } from '@tanstack/table-core';
import { MRT_ColumnDef } from 'mantine-react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// import { AdviceStatusCell } from '../advice-status-cell/advice-status-cell';
// import { StatusColumnFilter } from '../status-column-filter/status-column-filter';

import {
  ForcedResult,
  IntercultureCrop,
  IntercultureForm,
  YieldData,
} from '../../data-access/types/types';
import useValues from '../../hooks/use-values/values';

import { FieldNameCell } from './field-name-cell';
import { StackedCell } from './stacked-cell';
import { mapHarvestProofStatusToFormStatus } from '../../utils/map-harvest-proof-status';

import { useAuth } from '@auth/data-access';
import { hasAdminAccess, hasDistriAccess, FORM_STATUS, formatDate } from '@data-access';
import { StatusCell, StatusColumnFilter, StatusColumnFilterAdmin } from '@ui';

// eslint-disable-next-line @typescript-eslint/ban-types
type accessorKey = DeepKeys<IntercultureForm> | (string & {});
type ColVisibilityState = Partial<Record<accessorKey, boolean>>;

export const initialColState: ColVisibilityState = {
  'field.thumbnail': true,
  'field.name': true,
  'field.farm.name': true,

  is_harvested: false,
  informed_yield: false,
  seed_date: false,
  harvest_date: false,
  variety: false,
  'previous_crop.informed.name': false,
  inexistant: false,
  interculture_crop: false,
  previous_crop: false,
  previous_crop_harvest_date: false,
  yield_data: false,
  status_seed: false,
  status_harvest: false,
  status_harvest_proof: false,
  status: false,
  forced_result: false,
};

export const sowingColState: ColVisibilityState = {
  ...initialColState,
  seed_date: true,
  variety: true,
  'previous_crop.informed.name': true,
  previous_crop_harvest_date: true,
  status_seed: true,
};

export const harvestColState: ColVisibilityState = {
  ...initialColState,
  is_harvested: true,
  informed_yield: true,
  harvest_date: true,
  status_harvest: true,
  status_harvest_proof: true,
};

export const resultsColState: ColVisibilityState = {
  ...initialColState,
  is_harvested: false,
  inexistant: true,
  interculture_crop: true,
  previous_crop: true,
  yield_data: true,
  status: true,
  forced_result: true,
};

interface UseTableParams {
  type: string;

  onOpenForceResult: (result: IntercultureForm) => void;
}

export const useTable = ({ type, onOpenForceResult }: UseTableParams) => {
  const { t } = useTranslation();

  const values = useValues({ type });
  const {user } = useAuth();

  const columns = useMemo<MRT_ColumnDef<IntercultureForm>[]>(
    () => [
      {
        accessorKey: 'field.thumbnail',
        enableColumnFilter: false,
        header: '',
        enableSorting: false,
        enableColumnActions: false,
        size: 32,
        Cell: ({ cell, row }) => {
          const thumb = cell.getValue<string>();
          if (thumb) {
            return <img alt="thumbnail" src={thumb} width="32" />;
          }
        },
      },
      {
        accessorKey: 'field.name',
        header: t('table.columns.field-name'),
        Cell: ({ cell, row }) => {
          const value = cell.getValue<string>();

          return <FieldNameCell crop={type} disabled={!hasAdminAccess(user?.user_access)} rowId={row.id} value={value} />;
        },
      },
      {
        accessorKey: 'organization.name',
        header: t('table.columns.organization-name'),
        minSize: 250,
      },
      {
        accessorKey: 'field.farm.name',
        header: t('table.columns.farm-name'),
        minSize: 250,
      },
      {
        accessorKey: 'is_harvested',
        header: t('table.columns.harvestedFarm'),
        Cell: ({ cell }) => {
          const value = cell.getValue<boolean>();
          return t(value ? 'common.yes' : 'common.no');
        },
        minSize: 250,
        filterVariant: 'checkbox',
      },
      {
        accessorKey: 'informed_yield',
        Cell: ({ cell }) =>
          cell.getValue<number>() ? `${cell.getValue<number>()} q/ha` : '-',
        header: t('table.columns.yield'),
        filterVariant: 'range',
        filterFn: 'inNumberRange',
      },
      {
        accessorKey: 'seed_date',
        header: t('table.columns.intercultureSeedDate'),
        Cell: ({ cell }) => {
          const date = cell.getValue<string>();
          return (
            <Text>
              {date
                ? formatDate({
                    date,
                    format: 'display',
                  })
                : ''}
            </Text>
          );
        },
        filterVariant: 'date-range',
      },
      {
        accessorKey: 'harvest_date',
        header: t('table.columns.intercultureHarvestDate'),
        Cell: ({ cell }) => {
          const date = cell.getValue<string>();
          return (
            <Text>
              {date
                ? formatDate({
                    date,
                    format: 'display',
                  })
                : ''}
            </Text>
          );
        },
        filterVariant: 'date-range',
      },
      {
        accessorKey: 'variety',
        accessorFn: (row) => row.variety?.name,
        header: t('table.columns.intercultureVariety'),
        filterVariant: 'select',
        mantineFilterSelectProps: {
          data: values.data?.options.variety,
        },
      },
      {
        accessorKey: 'previous_crop.informed.name',
        header: t('table.columns.previousCrop'),
        filterVariant: 'select',
        mantineFilterSelectProps: {
          data: values.data?.options.previous_crop,
        },
      },

      {
        accessorKey: 'inexistant',
        header: '',
        enableColumnFilter: false,
        enableColumnActions: false,
        enableSorting: false,
        minSize: 200,
        Cell: ({ cell }) => (
          <Stack gap={0}>
            <div>Renseigné</div>
            <div>Estimé</div>
          </Stack>
        ),
      },
      {
        accessorKey: 'interculture_crop',
        Cell: ({ cell }) => {
          const intercultureCrop = cell.getValue<IntercultureCrop | null>();

          return (
            <StackedCell
              status={intercultureCrop?.status}
              values={[
                intercultureCrop?.informed?.name ?? '-',
                intercultureCrop?.estimated?.name ?? '-',
              ]}
            />
          );
        },
        header: t('table.columns.intercultureCrop'),
        filterVariant: 'select',
        mantineFilterSelectProps: {
          data: values.data?.options.interculture_crop,
        },
      },
      // {
      //   accessorKey: 'previous_crop_harvest_date',
      //   header: t('table.columns.interculturePreviousHarvestDate'),
      //   Cell: ({ cell }) => {
      //     const date = cell.getValue<string>();
      //     return (
      //       <Text>
      //         {date
      //           ? formatDate({
      //               date,
      //               format: 'display',
      //             })
      //           : ''}
      //       </Text>
      //     );
      //   },
      //   filterVariant: 'date-range',
      // },
      {
        accessorKey: 'previous_crop',
        header: t('results.previousCrop'),
        Cell: ({ cell }) => {
          const value = cell.getValue<IntercultureCrop | null>();

          return (
            <StackedCell
              status={value?.status}
              values={[
                value?.informed?.name ?? '-',
                value?.estimated?.name ?? '-',
              ]}
            />
          );
        },
        filterVariant: 'select',
        mantineFilterSelectProps: {
          data: values.data?.options.previous_crop,
        },
      },
      {
        accessorKey: 'yield_data',
        header: t('table.columns.yield_farm'),
        Cell: ({ cell, row }) => {
          const yieldData = cell.getValue<YieldData | null>();

          return (
            <StackedCell
              status={yieldData?.status}
              suffix="t"
              values={[
                yieldData?.informed ?? null,
                yieldData?.estimated ?? null,
              ]}
            />
          );
        },
        filterVariant: 'range',
        filterFn: 'inNumberRange',
      },
      {
        accessorKey: 'status_seed',
        header: t('table.columns.status'),
        maxSize: 100,
        Cell: ({ cell }) => {
          const status = cell.getValue<FORM_STATUS>();

          return <StatusCell status={status} />;
        },
        Filter: ({ column, table }) => {
          return (
            <StatusColumnFilter
              onChange={(value) => column.setFilterValue(value)}
              onClear={() => column.setFilterValue(null)}
              value={column.getFilterValue() as FORM_STATUS}
            />
          );
        },
      },
      {
        accessorKey: 'status_harvest',
        header: t('table.columns.status'),
        maxSize: 100,
        Cell: ({ cell }) => {
          const status = cell.getValue<FORM_STATUS>();

          return <StatusCell status={status} />;
        },
        Filter: ({ column, table }) => {
          return (
            <StatusColumnFilter
              onChange={(value) => column.setFilterValue(value)}
              onClear={() => column.setFilterValue(null)}
              value={column.getFilterValue() as FORM_STATUS}
            />
          );
        },
      },
      {
        accessorKey: 'status_harvest_proof',
        header: t('harvest-proof.columns.proofStatus'),
        maxSize: 100,
        Cell: ({ cell }) => {
          const status = mapHarvestProofStatusToFormStatus(
            cell.getValue<string | null>(),
          );
          if (!status) {
            return '-';
          }
          return <StatusCell status={status} />;
        },
        Filter: ({ column }) => (
          <StatusColumnFilter
            onChange={(value) => column.setFilterValue(value)}
            onClear={() => column.setFilterValue(null)}
            value={column.getFilterValue() as FORM_STATUS}
          />
        ),
      },
      {
        accessorKey: 'status',
        header: t('common.validation'),
        maxSize: 100,
        Cell: ({ cell, row }) => {
          const status = cell.getValue<FORM_STATUS>();

          return (
            <StatusCell
              onClick={() => onOpenForceResult(row.original)}
              status={status}
            />
          );
        },
        Filter: ({ column, table }) => {
          return (
            <StatusColumnFilterAdmin
              onChange={(value) => column.setFilterValue(value)}
              onClear={() => column.setFilterValue(null)}
              value={column.getFilterValue() as FORM_STATUS}
            />
          );
        },
      },
      {
        accessorKey: 'forced_result',
        header: t('results.forcedResult'),
        filterVariant: 'checkbox',
        Cell: ({ cell, row }) =>
          cell.getValue() ? (
            <Tooltip
              label={cell.getValue<ForcedResult>().comment}
              multiline
              position="bottom"
              w={220}
            >
              <Button
                onClick={() => {
                  onOpenForceResult(row.original);
                }}
                variant="subtle"
              >
                {formatDate({
                  date: cell.getValue<ForcedResult>().date,
                  format: 'display',
                })}
              </Button>
            </Tooltip>
          ) : (
            '-'
          ),
      },
    ],
    [
      t,
      values.data?.options.variety,
      values.data?.options.previous_crop,
      values.data?.options.interculture_crop,
      type,
      onOpenForceResult,
    ],
  );

  return { columns };
};
